/**
 * scripts-components-cart.js
 * -----------------------------------------------------------------------
 * All cart mutations (add/change quantity/remove) go through
 * Shopify's REAL, native cart AJAX endpoints (/cart/add.js,
 * /cart/change.js, /cart/update.js) — no custom cart backend, no
 * external cart library, no client-side money/currency logic. Every
 * request asks for the "cart-drawer" and "cart-icon-bubble" sections (and
 * the cart page's own section when present) via Shopify's Section
 * Rendering API `sections` parameter, so every number the customer sees
 * is rendered fresh, server-side, from the real cart.
 *
 * Reuses window.AniMarket.dom (qs/qsa/debounce) and the existing
 * <am-drawer> custom element; does not modify either.
 * ----------------------------------------------------------------------- */
(function () {
  'use strict';

  var dom = (window.AniMarket && window.AniMarket.dom) || {};
  var routes = (window.AniMarket && window.AniMarket.routes) || {};
  var formatMoney = (window.AniMarket && window.AniMarket.formatMoney) || function (cents) { return '$' + (cents / 100).toFixed(2); };
  var qs = dom.qs || function (s, scope) { return (scope || document).querySelector(s); };
  var qsa = dom.qsa || function (s, scope) { return Array.from((scope || document).querySelectorAll(s)); };
  var debounce = dom.debounce || function (fn) { return fn; };

  var SECTION_IDS = ['cart-drawer'];

  /** Per-line-key in-flight lock to prevent race conditions on rapid clicks. */
  var pendingKeys = Object.create(null);

  function cartPageSectionId() {
    var page = qs('[data-cart-page]');
    return page ? page.getAttribute('data-cart-page-section-id') : null;
  }

  function sectionsParam() {
    var ids = SECTION_IDS.filter(function (id) {
      return !!document.getElementById(id);
    });
    var pageId = cartPageSectionId();
    if (pageId) ids.push(pageId);
    return ids.join(',');
  }

  /**
   * The cart-count badge lives inside sections/header.liquid, so it can't
   * be its own Section-Rendering-API section (a section can't render
   * another section). Refresh it with a plain read-only /cart.js fetch
   * instead — still zero custom cart state, just the real cart count.
   */
  function refreshCartCountBadges() {
    fetch(routes.cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        qsa('[data-cart-count-badge]').forEach(function (badge) {
          badge.textContent = cart.item_count;
          badge.hidden = cart.item_count <= 0;
        });
      });
  }

  /**
   * Keeps every rendered discount-progress message (product page,
   * collection page) in sync after an AJAX cart mutation. The cart
   * drawer/cart page copies already update for free via renderSections()
   * (server-rendered with the `money` filter); this only covers the
   * standalone instances outside those re-rendered sections. Same
   * read-only /cart.js fetch as refreshCartCountBadges — no new cart
   * state, and money is formatted with the shared AniMarket.formatMoney
   * helper (the shop's real format), never a hardcoded "$".
   */
  function refreshDiscountProgress() {
    var elements = qsa('[data-discount-progress]');
    if (!elements.length) return;
    var copy = (window.AniMarket && window.AniMarket.discountProgressText) || {};

    fetch(routes.cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        elements.forEach(function (el) {
          var threshold = Number(el.getAttribute('data-discount-progress-threshold-cents')) || 0;
          if (!threshold) return;
          var remaining = threshold - cart.total_price;
          var unlocked = remaining <= 0;
          var percent = Math.min(100, Math.max(0, (cart.total_price / threshold) * 100));

          var eyebrow = qs('[data-discount-progress-eyebrow]', el);
          var message = qs('[data-discount-progress-message]', el);
          var fill = qs('[data-discount-progress-fill]', el);

          if (eyebrow) eyebrow.textContent = unlocked ? copy.eyebrowUnlocked || '' : copy.eyebrowLocked || '';
          if (message) {
            // remainingTemplate embeds a <span> around the amount (see
            // general.discount_progress.remaining_html), so this must be
            // innerHTML, not textContent, to keep the amount highlighted.
            message.innerHTML = unlocked
              ? copy.unlockedText || ''
              : (copy.remainingTemplate || '').replace(/\{\{\s*amount\s*\}\}/, formatMoney(remaining));
          }
          if (fill) fill.style.width = percent + '%';
          var track = qs('[role="progressbar"]', el);
          if (track) track.setAttribute('aria-valuenow', String(percent));
        });
      });
  }

  function announce(message) {
    qsa('[data-cart-live-region]').forEach(function (region) {
      region.textContent = message;
    });
  }

  function liveRegionText(attribute) {
    var region = qs('[data-cart-live-region]');
    return (region && region.getAttribute(attribute)) || '';
  }

  function summaryText(attribute) {
    var summary = qs('[data-cart-status]');
    return (summary && summary.getAttribute(attribute)) || '';
  }

  function setStatus(message, isError) {
    qsa('[data-cart-status]').forEach(function (el) {
      el.textContent = message || '';
      if (isError) {
        el.setAttribute('data-state', 'error');
      } else {
        el.removeAttribute('data-state');
      }
    });
  }

  function toggleLoading(scopeEl, isLoading) {
    if (!scopeEl) return;
    scopeEl.classList.toggle('is-updating', !!isLoading);
    qsa('button, input', scopeEl).forEach(function (el) {
      el.disabled = !!isLoading;
    });
  }

  function setButtonLoading(button, isLoading) {
    if (!button) return;
    button.classList.toggle('btn--loading', !!isLoading);
    button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    button.disabled = !!isLoading;
  }

  /**
   * Parse a Section Rendering API response and swap each returned
   * section's markup into every matching `#shopify-section-{id}` on the
   * page (there can be more than one, e.g. the drawer section also
   * exists on the /cart page since the header renders everywhere).
   * @param {Object} sections - `response.sections`, keyed by section id
   */
  function renderSections(sections) {
    if (!sections) return;
    var parser = new DOMParser();
    Object.keys(sections).forEach(function (id) {
      var html = sections[id];
      if (!html) return;
      var doc = parser.parseFromString(html, 'text/html');
      var newSection = doc.getElementById('shopify-section-' + id);
      var currentSection = document.getElementById('shopify-section-' + id);
      if (newSection && currentSection) {
        // Replacing the cart section also replaces its native <dialog>.
        // Remember the drawer state first so quantity changes do not make
        // an open cart appear to close after the server-rendered refresh.
        var currentDrawer = qs('[data-cart-drawer]', currentSection);
        var currentDialog = currentDrawer && qs('dialog', currentDrawer);
        var wasDrawerOpen = !!(currentDialog && currentDialog.open);
        var drawerBody = currentDrawer && qs('.drawer__body', currentDrawer);
        var drawerScrollTop = drawerBody ? drawerBody.scrollTop : 0;

        currentSection.innerHTML = newSection.innerHTML;

        if (wasDrawerOpen) {
          var refreshedDrawer = qs('[data-cart-drawer]', currentSection);
          if (refreshedDrawer && typeof refreshedDrawer.open === 'function') {
            refreshedDrawer.open();
            var refreshedBody = qs('.drawer__body', refreshedDrawer);
            if (refreshedBody) refreshedBody.scrollTop = drawerScrollTop;
          }
        }
      }
    });
  }

  function cartRequest(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var message = (data && (data.description || data.message)) || 'error';
          return Promise.reject(new Error(message));
        }
        return data;
      });
    });
  }

  // ---------------------------------------------------------------------
  // Add to cart
  // ---------------------------------------------------------------------

  function showShoppingFeedback(message, isError) {
    var feedback = qs('[data-shopping-feedback]');
    if (!feedback) return;
    qs('[data-shopping-feedback-text]', feedback).textContent = message;
    feedback.classList.toggle('shopping-feedback--error', !!isError);
    feedback.hidden = false;
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-shopping-feedback-close]')) {
      qs('[data-shopping-feedback]').hidden = true;
    }
  });

  function handleAddToCartSubmit(event) {
    var form = event.target;
    if (!form.matches('form[data-type="add-to-cart-form"]')) return;
    event.preventDefault();

    var submitter = event.submitter || qs('[type="submit"]', form);
    if (submitter && submitter.getAttribute('aria-busy') === 'true') return;
    var feedback = qs('[data-shopping-feedback]');
    if (feedback) feedback.hidden = true;
    setButtonLoading(submitter, true);
    setStatus('');

    var formData = new FormData(form);
    var body = {
      items: [
        {
          id: formData.get('id'),
          quantity: Number(formData.get('quantity')) || 1,
        },
      ],
      sections: sectionsParam(),
      sections_url: window.location.pathname,
    };

    // Forward any line item properties (name="properties[...]") present
    // on the form, e.g. personalization/character/custom-text fields.
    var properties = {};
    formData.forEach(function (value, key) {
      var match = key.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = value;
    });
    if (Object.keys(properties).length) {
      body.items[0].properties = properties;
    }
    var sellingPlan = formData.get('selling_plan');
    if (sellingPlan) body.items[0].selling_plan = sellingPlan;

    cartRequest(routes.cartAddUrl, body)
      .then(function (data) {
        renderSections(data.sections);
        refreshCartCountBadges();
        refreshDiscountProgress();
        announce(liveRegionText('data-added-text'));
        if (shouldOpenDrawerAfterAdd()) openCartDrawer();
        else showShoppingFeedback(window.AniMarket.addedToCartText, false);
      })
      .catch(function (error) {
        setStatus(error.message || summaryText('data-error-text'), true);
        showShoppingFeedback(error.message || summaryText('data-error-text'), true);
      })
      .finally(function () {
        setButtonLoading(submitter, false);
      });
  }

  function shouldOpenDrawerAfterAdd() {
    var drawerRoot = qs('[data-cart-drawer]');
    return !!drawerRoot && drawerRoot.getAttribute('data-cart-open-after-add') === 'true';
  }

  function openCartDrawer() {
    var drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  // ---------------------------------------------------------------------
  // Quantity change / remove (delegated within [data-cart-items])
  // ---------------------------------------------------------------------

  function changeLineQuantity(key, quantity, container, revertTo, revertInput) {
    if (pendingKeys[key]) return;
    pendingKeys[key] = true;

    var itemEl = container.closest('[data-cart-item]');
    toggleLoading(itemEl, true);
    setStatus('');
    announce(liveRegionText('data-updating-text'));

    cartRequest(routes.cartChangeUrl, {
      id: key,
      quantity: quantity,
      sections: sectionsParam(),
      sections_url: window.location.pathname,
    })
      .then(function (data) {
        renderSections(data.sections);
        refreshCartCountBadges();
        refreshDiscountProgress();
      })
      .catch(function (error) {
        setStatus(error.message || summaryText('data-error-text'), true);
        if (itemEl) toggleLoading(itemEl, false);
        // The request never reached the server, so revert the optimistic
        // input value back to what the real cart still holds.
        if (revertInput && revertTo != null) revertInput.value = revertTo;
      })
      .finally(function () {
        delete pendingKeys[key];
      });
  }

  function handleCartItemsClick(event) {
    var minusButton = event.target.closest('[data-cart-quantity-minus]');
    var plusButton = event.target.closest('[data-cart-quantity-plus]');
    var removeButton = event.target.closest('[data-cart-remove]');

    if (minusButton || plusButton) {
      var stepper = event.target.closest('[data-cart-quantity]');
      var input = qs('[data-cart-quantity-input]', stepper);
      var key = stepper.getAttribute('data-cart-key');
      var previous = Number(input.value) || 0;
      var next = Math.max(0, previous + (plusButton ? 1 : -1));
      input.value = next;
      changeLineQuantity(key, next, stepper, previous, input);
      return;
    }

    if (removeButton) {
      var key2 = removeButton.getAttribute('data-cart-key');
      changeLineQuantity(key2, 0, removeButton);
    }
  }

  var handleCartQuantityInput = debounce(function (event) {
    var input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;
    var stepper = input.closest('[data-cart-quantity]');
    var key = stepper.getAttribute('data-cart-key');
    var quantity = Math.max(0, Number(input.value) || 0);
    changeLineQuantity(key, quantity, stepper);
  }, 500);

  function handleCartItemsChange(event) {
    if (event.target.matches('[data-cart-quantity-input]')) {
      handleCartQuantityInput(event);
    }
  }

  // ---------------------------------------------------------------------
  // Discount code (native /discount/[code] redirect — no fetch, no fake
  // in-cart validation; Shopify only validates discount codes at
  // checkout, so we send the customer straight there with it applied).
  // ---------------------------------------------------------------------

  function handleDiscountCodeApply(event) {
    var button = event.target.closest('[data-cart-discount-code-apply]');
    if (!button) return;
    var input = qs('[data-cart-discount-code-input]', button.closest('[data-cart-summary]'));
    var code = input && input.value.trim();
    if (!code) return;
    var root = routes.rootUrl || '/';
    window.location.href =
      root + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/checkout');
  }

  // ---------------------------------------------------------------------
  // Wire up delegated listeners once, at document level.
  // ---------------------------------------------------------------------

  document.addEventListener('submit', handleAddToCartSubmit);

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-cart-items]')) handleCartItemsClick(event);
    handleDiscountCodeApply(event);
  });

  document.addEventListener('change', function (event) {
    if (event.target.closest('[data-cart-items]')) handleCartItemsChange(event);
  });
})();
